/**
 * Wizard de criação de Template WhatsApp WABA
 * Layout premium de 3 colunas: Sidebar + Formulário + Preview
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, X, Loader2, Info, Settings, MessageSquare } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
// Detector de emojis para validação defensiva no submit (defesa contra bypass)
import { containsEmoji } from '@/lib/templates/sanitize-emojis';
import { logger } from '@/lib/logger';

// Componentes do wizard
import {
  TemplateWizardSidebar,
  TemplatePreviewPanel,
  TemplateConfigStep,
  TemplateMessageStep,
  WizardStep,
  OfficialInbox,
  TemplateClassification,
  TemplateButton,
  HeaderFormat,
} from './wizard';

// Definição dos steps
const STEP_CONFIG = {
  config: {
    icon: Settings,
    title: 'Configurações do Modelo',
    description: 'Defina inbox, nome, categoria e idioma do template',
  },
  message: {
    icon: MessageSquare,
    title: 'Cadastro da Mensagem',
    description: 'Configure header, corpo, rodapé e botões',
  },
};

interface CreateTemplateWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inboxes: OfficialInbox[];
  onSuccess: () => void;
}

export function CreateTemplateWizard({
  open,
  onOpenChange,
  inboxes,
  onSuccess,
}: CreateTemplateWizardProps) {
  const { currentTenant } = useAuth();
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState<WizardStep>('config');
  
  // Estado do formulário - Step 1: Configurações
  const [inboxId, setInboxId] = useState('');
  const [name, setName] = useState('');
  const [category, setCategory] = useState('UTILITY');
  const [language, setLanguage] = useState('pt_BR');
  const [classificationId, setClassificationId] = useState<string | null>(null);
  const [classifications, setClassifications] = useState<TemplateClassification[]>([]);
  
  // Estado do formulário - Step 2: Mensagem
  const [headerFormat, setHeaderFormat] = useState<HeaderFormat>('NONE');
  const [headerText, setHeaderText] = useState('');
  const [headerMediaUrl, setHeaderMediaUrl] = useState('');
  /** Nome do arquivo de mídia selecionado para exibição */
  const [headerMediaFilename, setHeaderMediaFilename] = useState('');
  const [bodyText, setBodyText] = useState('');
  const [footerText, setFooterText] = useState('');
  const [buttons, setButtons] = useState<TemplateButton[]>([]);
  
  // Estado para valores de exemplo das variáveis do body (exigido pela Meta API)
  const [exampleValues, setExampleValues] = useState<Record<string, string>>({});

  /**
   * Atualiza valor de exemplo de uma variável do body
   */
  const updateExampleValue = (variable: string, value: string) => {
    setExampleValues(prev => ({
      ...prev,
      [variable.replace(/[{}]/g, '')]: value,
    }));
  };

  /**
   * Busca classificações ao abrir o wizard
   */
  useEffect(() => {
    if (open && currentTenant?.id) {
      fetchClassifications();
    }
  }, [open, currentTenant?.id]);

  const fetchClassifications = async () => {
    if (!currentTenant?.id) return;
    
    try {
      const { data, error } = await supabase
        .from('template_classifications')
        .select('id, name, color')
        .eq('tenant_id', currentTenant.id)
        .order('name');

      if (error) throw error;
      setClassifications(data || []);
    } catch (error) {
      logger.error('[CreateTemplateWizard] Erro ao buscar classificações:', error);
    }
  };

  /**
   * Reseta o formulário
   */
  const resetForm = useCallback(() => {
    setCurrentStep('config');
    setInboxId('');
    setName('');
    setCategory('UTILITY');
    setLanguage('pt_BR');
    setClassificationId(null);
    setHeaderFormat('NONE');
    setHeaderText('');
    setHeaderMediaUrl('');
    setHeaderMediaFilename('');
    setBodyText('');
    setFooterText('');
    setButtons([]);
    setExampleValues({});
  }, []);

  /**
   * Fecha o wizard
   */
  const handleClose = useCallback(() => {
    resetForm();
    onOpenChange(false);
  }, [resetForm, onOpenChange]);

  /**
   * Verifica se o Step 1 está válido
   */
  const isStep1Valid = (): boolean => {
    return inboxId !== '' && name.trim() !== '';
  };

  /**
   * Verifica se o Step 2 está válido
   */
  const isStep2Valid = (): boolean => {
    return bodyText.trim() !== '';
  };

  /**
   * Verifica se um step foi completado
   */
  const isStepCompleted = (step: WizardStep): boolean => {
    if (step === 'config') {
      return currentStep === 'message' && isStep1Valid();
    }
    return false;
  };

  /**
   * Verifica se um step está desabilitado
   */
  const isStepDisabled = (step: WizardStep): boolean => {
    if (step === 'message') {
      return !isStep1Valid();
    }
    return false;
  };

  /**
   * Navega para o próximo step
   */
  const handleNext = () => {
    if (currentStep === 'config') {
      if (!isStep1Valid()) {
        toast.error('Preencha todos os campos obrigatórios');
        return;
      }
      setCurrentStep('message');
    }
  };

  /**
   * Volta para o step anterior
   */
  const handleBack = () => {
    if (currentStep === 'message') {
      setCurrentStep('config');
    }
  };

  /**
   * Navega para um step específico
   */
  const handleStepClick = (step: WizardStep) => {
    if (!isStepDisabled(step)) {
      setCurrentStep(step);
    }
  };

  /**
   * Monta array de componentes para a Meta API
   */
  const buildComponents = () => {
    const components: any[] = [];

    // Header
    if (headerFormat !== 'NONE') {
      const header: any = { type: 'HEADER', format: headerFormat };
      if (headerFormat === 'TEXT') {
        header.text = headerText;
      } else if (headerMediaUrl) {
        header.example = { header_handle: [headerMediaUrl] };
      }
      components.push(header);
    }

    // Body (obrigatório) - adiciona exemplos se houver variáveis
    const bodyComponent: any = {
      type: 'BODY',
      text: bodyText,
    };

    // Detecta variáveis no body e adiciona campo example exigido pela Meta API
    const bodyVars = bodyText.match(/\{\{(\d+)\}\}/g);
    if (bodyVars && bodyVars.length > 0) {
      const uniqueVars = [...new Set(bodyVars)].sort();
      const exampleTexts = uniqueVars.map(v => {
        const key = v.replace(/[{}]/g, '');
        return exampleValues[key] || `exemplo_${key}`;
      });
      bodyComponent.example = { body_text: [exampleTexts] };
    }

    components.push(bodyComponent);

    // Footer
    if (footerText.trim()) {
      components.push({
        type: 'FOOTER',
        text: footerText,
      });
    }

    // Buttons
    if (buttons.length > 0) {
      components.push({
        type: 'BUTTONS',
        buttons: buttons.filter(b => b.text.trim()).map(b => {
          const btn: any = { type: b.type, text: b.text };
          
          if (b.type === 'URL' && b.url) {
            // Evita duplicar {{1}} caso o usuário já tenha incluído na URL
            const urlBase = b.url || '';
            btn.url = b.url_type === 'dynamic'
              ? (urlBase.includes('{{1}}') ? urlBase : `${urlBase}{{1}}`)
              : urlBase;
            if (b.url_type === 'dynamic' && b.example_value) {
              btn.example = [b.example_value];
            }
          }
          
          if (b.type === 'PHONE_NUMBER' && b.phone_number) {
            btn.phone_number = b.phone_number;
          }
          
          // COPY_CODE - Botão para copiar código OTP
          if (b.type === 'COPY_CODE') {
            btn.example = b.code_example ? [b.code_example] : ['123456'];
          }
          
          // ORDER_DETAILS - PIX Brasil
          if (b.type === 'ORDER_DETAILS') {
            btn.order_details = {
              reference_id: crypto.randomUUID(),
              type: 'digital-goods',
              payment_type: 'br',
              payment_settings: [{
                type: 'pix_dynamic_code',
                pix_dynamic_code: {
                  code: b.pix_code || '',
                  merchant_name: b.merchant_name || '',
                  key: b.pix_key || '',
                  key_type: b.pix_key_type || 'CNPJ',
                }
              }],
              currency: 'BRL',
              total_amount: {
                value: Math.round((b.amount_value || 0) * 100),
                offset: 100
              },
              order: {
                items: [{ name: b.item_name || 'Pagamento' }]
              }
            };
          }
          
          return btn;
        }),
      });
    }

    return components;
  };

  /**
   * Extrai variáveis do texto
   */
  const extractVariables = (): string[] => {
    const allText = `${headerText} ${bodyText} ${footerText}`;
    const matches = allText.match(/\{\{(\d+)\}\}/g);
    if (!matches) return [];
    return [...new Set(matches)].sort();
  };

  /**
   * Valida botões
   */
  const validateButtons = (): boolean => {
    for (let i = 0; i < buttons.length; i++) {
      const btn = buttons[i];
      
      if (!btn.text.trim()) {
        toast.error(`Botão ${i + 1}: Texto é obrigatório`);
        return false;
      }
      
      if (btn.type === 'URL' && !btn.url?.trim()) {
        toast.error(`Botão ${i + 1}: URL é obrigatória`);
        return false;
      }
      
      if (btn.type === 'URL' && btn.url_type === 'dynamic' && !btn.example_value?.trim()) {
        toast.error(`Botão ${i + 1}: Valor de exemplo é obrigatório para URL dinâmica`);
        return false;
      }
      
      if (btn.type === 'PHONE_NUMBER' && !btn.phone_number?.trim()) {
        toast.error(`Botão ${i + 1}: Número de telefone é obrigatório`);
        return false;
      }
      
      if (btn.type === 'COPY_CODE' && !btn.code_example?.trim()) {
        toast.error(`Botão ${i + 1}: Exemplo de código OTP é obrigatório para aprovação`);
        return false;
      }
      
      if (btn.type === 'ORDER_DETAILS') {
        if (!btn.pix_code?.trim()) {
          toast.error(`Botão ${i + 1}: Código PIX é obrigatório`);
          return false;
        }
        if (!btn.merchant_name?.trim()) {
          toast.error(`Botão ${i + 1}: Nome do recebedor é obrigatório`);
          return false;
        }
        if (!btn.pix_key?.trim()) {
          toast.error(`Botão ${i + 1}: Chave PIX é obrigatória`);
          return false;
        }
        if (!btn.amount_value || btn.amount_value <= 0) {
          toast.error(`Botão ${i + 1}: Valor do pagamento deve ser maior que zero`);
          return false;
        }
      }
    }
    return true;
  };

  /**
   * Submete o template
   */
  const handleSubmit = async () => {
    // Validações básicas
    if (!inboxId) {
      toast.error('Selecione uma inbox');
      return;
    }
    if (!name.trim()) {
      toast.error('Nome é obrigatório');
      return;
    }
    if (!bodyText.trim()) {
      toast.error('Corpo da mensagem é obrigatório');
      return;
    }

    // Valida formato do nome
    const nameRegex = /^[a-z0-9_]+$/;
    if (!nameRegex.test(name)) {
      toast.error('Nome deve conter apenas letras minúsculas, números e underscore');
      return;
    }

    // ----------------------------------------------------------------------
    // Validação defensiva contra emojis (defesa em profundidade).
    // O sanitizador já age na digitação, mas se algo passar (paste programático,
    // injeção via DevTools, etc.) bloqueamos antes de enviar para o Meta.
    // ----------------------------------------------------------------------
    const emojiFields: string[] = [];
    if (containsEmoji(headerText)) emojiFields.push('Cabeçalho');
    // Corpo permite emojis (Meta aceita emojis no BODY de templates)
    if (containsEmoji(footerText)) emojiFields.push('Rodapé');
    Object.values(exampleValues || {}).forEach((v) => {
      if (containsEmoji(v) && !emojiFields.includes('Valores de exemplo')) {
        emojiFields.push('Valores de exemplo');
      }
    });
    buttons.forEach((btn, idx) => {
      const buttonHasEmoji =
        containsEmoji(btn.text) ||
        containsEmoji(btn.example_value) ||
        containsEmoji(btn.code_example) ||
        containsEmoji(btn.merchant_name) ||
        containsEmoji(btn.item_name);
      if (buttonHasEmoji) emojiFields.push(`Botão ${idx + 1}`);
    });
    if (emojiFields.length > 0) {
      toast.error(
        `Remova os emojis antes de salvar. Campos com emoji: ${emojiFields.join(', ')}`
      );
      return;
    }

    // Validação Meta API: variáveis não podem estar no início ou fim do body
    const trimmedBody = bodyText.trim();
    if (/^\{\{\d+\}\}/.test(trimmedBody) || /\{\{\d+\}\}$/.test(trimmedBody)) {
      toast.error('O corpo da mensagem não pode começar nem terminar com uma variável (ex: {{1}}). Adicione texto antes e/ou depois da variável.');
      return;
    }
    
    // Valida exemplos de variáveis do body
    const detectedVars = bodyText.match(/\{\{(\d+)\}\}/g);
    if (detectedVars && detectedVars.length > 0) {
      const uniqueVars = [...new Set(detectedVars)];
      for (const v of uniqueVars) {
        const key = v.replace(/[{}]/g, '');
        if (!exampleValues[key]?.trim()) {
          toast.error(`Preencha o valor de exemplo para a variável ${v}`);
          return;
        }
      }
    }
    
    // Valida botões
    if (!validateButtons()) {
      return;
    }

    try {
      setLoading(true);

      const components = buildComponents();
      const variables = extractVariables();

      // Detecta provider da inbox selecionada
      const selectedInbox = inboxes.find(i => i.id === inboxId);
      const providerSlug = selectedInbox?.provider_slug;

      logger.info('[CreateTemplateWizard]', `Criando template via provider: ${providerSlug || 'local'}`);

      // Se for meta-whatsapp, cria diretamente na Meta via Edge Function
      if (providerSlug === 'meta-whatsapp') {
        const { data: sessionData } = await supabase.auth.getSession();
        if (!sessionData?.session?.access_token) {
          toast.error('Sessão expirada. Faça login novamente.');
          return;
        }

        const { data, error } = await supabase.functions.invoke('manage-template', {
          body: {
            inbox_id: inboxId,
            action: 'create',
            template_name: name,
            template_data: {
              name,
              category,
              language,
              components,
            },
          },
        });

        if (error) {
          logger.error('[CreateTemplateWizard] Erro ao criar template na Meta:', error);
          toast.error(error.message || 'Erro ao criar template na Meta');
          return;
        }

        if (!data?.success) {
          toast.error(data?.error || 'Erro ao criar template na Meta');
          return;
        }

        toast.success('Template enviado para aprovação da Meta!');
        logger.info('[CreateTemplateWizard]', `Template ${name} criado na Meta com ID: ${data?.meta_template_id}`);
      } else {
        // Para outros providers: salva localmente
        const templateId = `local_${Date.now()}`;

        const { error } = await supabase
          .from('whatsapp_templates')
          .insert([{
            tenant_id: currentTenant!.id,
            inbox_id: inboxId,
            template_id: templateId,
            name,
            language,
            category,
            status: 'PENDING',
            components,
            variables: variables.length > 0 ? variables : null,
            classification_id: classificationId,
          }]);

        if (error) throw error;

        toast.success('Template criado! Submeta para aprovação via painel do seu provedor.');
        logger.info('[CreateTemplateWizard]', `Template ${name} criado localmente`);
      }
      
      resetForm();
      onSuccess();
    } catch (error: any) {
      logger.error('[CreateTemplateWizard] Erro ao criar:', error);
      
      if (error.code === '23505') {
        toast.error('Já existe um template com este nome nesta inbox');
      } else {
        toast.error('Erro ao criar template');
      }
    } finally {
      setLoading(false);
    }
  };

  // Dados do step atual
  const currentStepConfig = STEP_CONFIG[currentStep];
  const StepIcon = currentStepConfig.icon;

  // Resumo para a sidebar
  const selectedInbox = inboxes.find(i => i.id === inboxId);
  const summary = {
    inboxName: selectedInbox?.name,
    category: category,
    language: language,
    buttonCount: buttons.length,
    hasHeader: headerFormat !== 'NONE',
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent 
        className="max-w-6xl max-h-[90vh] h-[90vh] p-0 gap-0 flex flex-col"
        hideCloseButton
        preventCloseOnOutsideInteraction
      >
        <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] lg:grid-cols-[280px_1fr_320px] h-full min-h-0 flex-1">
          
          {/* SIDEBAR - Steps + Resumo */}
          <TemplateWizardSidebar
            currentStep={currentStep}
            onStepClick={handleStepClick}
            isStepCompleted={isStepCompleted}
            isStepDisabled={isStepDisabled}
            summary={summary}
          />
          
          {/* CONTEÚDO PRINCIPAL */}
          <main className="flex flex-col h-full min-h-0">
            
            {/* Header do step atual */}
            <DialogHeader className="flex-shrink-0 px-6 py-5 border-b bg-background">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <StepIcon className="h-6 w-6 text-primary" />
                </div>
                <div className="flex-1">
                  <DialogTitle className="text-lg font-semibold">
                    {currentStepConfig.title}
                  </DialogTitle>
                  <DialogDescription className="text-sm">
                    {currentStepConfig.description}
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>
            
            {/* Informação importante - apenas no step 1 */}
            {currentStep === 'config' && (
              <div className="flex-shrink-0 mx-6 mt-4">
                <div className="flex items-start gap-3 p-3 rounded-lg bg-blue-500/10 text-blue-600 text-sm">
                  <Info className="h-5 w-5 flex-shrink-0 mt-0.5" />
                  <div>
                    <strong>Importante:</strong> Templates precisam ser aprovados pela Meta antes 
                    de serem usados. O processo pode levar até 24 horas.
                  </div>
                </div>
              </div>
            )}
            
            {/* Área de conteúdo scrollável */}
            <div className="flex-1 overflow-y-auto min-h-0 p-6">
              {currentStep === 'config' && (
                <TemplateConfigStep
                  inboxId={inboxId}
                  name={name}
                  category={category}
                  language={language}
                  classificationId={classificationId}
                  onInboxIdChange={setInboxId}
                  onNameChange={setName}
                  onCategoryChange={setCategory}
                  onLanguageChange={setLanguage}
                  onClassificationIdChange={setClassificationId}
                  inboxes={inboxes}
                  classifications={classifications}
                />
              )}
              
              {currentStep === 'message' && (
                <TemplateMessageStep
                  headerFormat={headerFormat}
                  headerText={headerText}
                  headerMediaUrl={headerMediaUrl}
                  bodyText={bodyText}
                  footerText={footerText}
                  buttons={buttons}
                  exampleValues={exampleValues}
                  onHeaderFormatChange={setHeaderFormat}
                  onHeaderTextChange={setHeaderText}
                  onHeaderMediaUrlChange={setHeaderMediaUrl}
                  onBodyTextChange={setBodyText}
                  onFooterTextChange={setFooterText}
                  onButtonsChange={setButtons}
                  onExampleValueChange={updateExampleValue}
                />
              )}
            </div>
            
            {/* Footer com navegação */}
            <div className="flex-shrink-0 bg-background px-6 py-4 border-t flex justify-between">
              <Button
                variant="outline"
                onClick={currentStep === 'config' ? handleClose : handleBack}
                disabled={loading}
              >
                {currentStep === 'config' ? (
                  <>
                    <X className="h-4 w-4 mr-2" />
                    Cancelar
                  </>
                ) : (
                  <>
                    <ChevronLeft className="h-4 w-4 mr-2" />
                    Voltar
                  </>
                )}
              </Button>
              
              {/* Indicador mobile */}
              <div className="flex md:hidden items-center gap-1">
                <div className={`w-2 h-2 rounded-full ${currentStep === 'config' ? 'bg-primary w-4' : 'bg-green-500'}`} />
                <div className={`w-2 h-2 rounded-full ${currentStep === 'message' ? 'bg-primary w-4' : 'bg-muted'}`} />
              </div>
              
              {currentStep === 'config' ? (
                <Button
                  onClick={handleNext}
                  disabled={!isStep1Valid()}
                >
                  Próximo
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={loading || !isStep2Valid()}
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Criando...
                    </>
                  ) : (
                    'Criar Template'
                  )}
                </Button>
              )}
            </div>
          </main>
          
          {/* PREVIEW - WhatsApp em tempo real */}
          <TemplatePreviewPanel
            headerFormat={headerFormat}
            headerText={headerText}
            headerMediaUrl={headerMediaUrl}
            bodyText={bodyText}
            footerText={footerText}
            buttons={buttons}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
